import { capitalize } from "lopermut"
import { useCountries } from "modules/shared/hooks/useCountries"
import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  OthersDynamicSegmentName,
  PredefinedSegmentGroups,
  SegmentGroupResponseDataSchema,
  UnknownSegmentName,
} from "schema"
import z from "zod"

export function usePredefinedSegmentTranslations() {
  const { t, i18n } = useTranslation()

  const { labels: countryLabels } = useCountries()

  const languageNames = useMemo(
    () => new Intl.DisplayNames([i18n.language], { type: "language" }),
    [i18n.language],
  )

  const predefinedSegmentTranslations = useMemo(() => {
    return {
      [PredefinedSegmentGroups.satisfaction]: {
        advocates: t("dashboard.voiceOfCustomer.advocates"),
        neutrals: t("dashboard.voiceOfCustomer.neutrals"),
        detractors: t("dashboard.voiceOfCustomer.detractors"),
      },
      [PredefinedSegmentGroups.country]: {
        ...countryLabels,
        [OthersDynamicSegmentName]: t("shared.others"),
      },
      [PredefinedSegmentGroups.purchaseRecency]: {
        last7Days: t("segments.predefined.kind.purchaseRecency.segments.last7Days"),
        from7To30Days: t("segments.predefined.kind.purchaseRecency.segments.from7To30Days"),
        from30To90Days: t("segments.predefined.kind.purchaseRecency.segments.from30To90Days"),
        from90To180Days: t("segments.predefined.kind.purchaseRecency.segments.from90To180Days"),
        from180To360Days: t("segments.predefined.kind.purchaseRecency.segments.from180To360Days"),
        from360To720Days: t("segments.predefined.kind.purchaseRecency.segments.from360To720Days"),
        moreThan720Days: t("segments.predefined.kind.purchaseRecency.segments.moreThan720Days"),
        noPurchases: t("segments.predefined.kind.purchaseRecency.segments.noPurchases"),
      },
      [PredefinedSegmentGroups.rfm]: {
        champions: t("segments.predefined.kind.rfm.segments.champions"),
        loyal: t("segments.predefined.kind.rfm.segments.loyal"),
        active: t("segments.predefined.kind.rfm.segments.active"),
        new: t("segments.predefined.kind.rfm.segments.new"),
        promising: t("segments.predefined.kind.rfm.segments.promising"),
        needsAttention: t("segments.predefined.kind.rfm.segments.needsAttention"),
        almostLost: t("segments.predefined.kind.rfm.segments.almostLost"),
        atRisk: t("segments.predefined.kind.rfm.segments.atRisk"),
        previouslyLoyal: t("segments.predefined.kind.rfm.segments.previouslyLoyal"),
        dormant: t("segments.predefined.kind.rfm.segments.dormant"),
        prospects: t("segments.predefined.kind.rfm.segments.prospects"),
      },
    } as Record<string, Record<string, string>>
  }, [t, countryLabels])

  const getSegmentTranslation = useCallback(
    (segmentName: string, segmentGroup?: z.infer<typeof SegmentGroupResponseDataSchema>) => {
      if (segmentName === OthersDynamicSegmentName) {
        return t("segments.others")
      }

      if (segmentName === UnknownSegmentName) {
        return t("segments.unknown")
      }

      if (segmentGroup?.isPredefined && segmentGroup.name === PredefinedSegmentGroups.language) {
        return capitalize(languageNames.of(segmentName)) ?? segmentName
      }

      if (segmentGroup?.isPredefined) {
        return (
          predefinedSegmentTranslations[
            segmentGroup.name as keyof typeof predefinedSegmentTranslations
          ]?.[segmentName] || segmentName
        )
      }

      return segmentName
    },
    [predefinedSegmentTranslations, languageNames, t],
  )

  return { getSegmentTranslation }
}
